'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { bytesPerSample, viewerExtension, type TraceFormat } from '@/lib/ctg/traceBinary'

import { createFhrViewer, disposeFhrViewerHost } from './fhrViewer'
import styles from './richText.module.css'

export type CtgTraceEmbedLabels = { trace: string; enlargeTrace: string; close: string; loadingTrace: string; traceError: string }

type Props = {
  // Signed URL of the cropped binary (4-byte header) and the visible markers already re-indexed on the crop.
  src: string
  format: TraceFormat
  markers: string
  display: 'thumbnail' | 'fullWidth'
  title: string
  caption?: string | null
  labels: CtgTraceEmbedLabels
  viewerLabels: Record<string, string>
}

// CTG trace inside rich text: a small preview that opens a large viewer in a modal, or a full-width viewer.
export function CtgTraceEmbed({ src, format, markers, display, title, caption, labels, viewerLabels }: Props) {
  const inlineHost = useRef<HTMLDivElement>(null)
  const modalHost = useRef<HTMLDivElement>(null)
  const dialog = useRef<HTMLDialogElement>(null)
  const buffer = useRef<ArrayBuffer | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const thumbnail = display === 'thumbnail'

  const mountViewer = useCallback(
    async (host: HTMLElement, height: number) => {
      if (!buffer.current) return
      // Height through CSS, not the `height` option: setHeight() also resizes a same-origin parent iframe (the
      // admin live preview) to the viewer height.
      host.style.height = `${height}px`
      const viewer = await createFhrViewer(host, { labels: viewerLabels })
      // A copy: the viewer keeps the buffer it is given.
      viewer.loadBuffer(buffer.current.slice(0), viewerExtension(format), { headerBytes: 4, bytesPerSample: bytesPerSample(format) })
      if (markers) viewer.loadMarkers(markers)
    },
    [format, markers, viewerLabels],
  )

  useEffect(() => {
    let cancelled = false
    const host = inlineHost.current
    ;(async () => {
      try {
        const response = await fetch(src, { credentials: 'same-origin' })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        buffer.current = await response.arrayBuffer()
        if (cancelled || !host) return
        await mountViewer(host, thumbnail ? 200 : 380)
        if (!cancelled) setStatus('ready')
      } catch {
        if (!cancelled) setStatus('error')
      }
    })()
    return () => {
      cancelled = true
      disposeFhrViewerHost(host)
    }
  }, [src, thumbnail, mountViewer])

  const openModal = () => {
    dialog.current?.showModal()
    // The viewer measures its host: create it once the dialog is laid out.
    requestAnimationFrame(() => {
      if (modalHost.current) void mountViewer(modalHost.current, Math.max(260, Math.round(window.innerHeight * 0.62)))
    })
  }

  return (
    <figure className={`${styles.trace} ${thumbnail ? styles.traceThumbnail : styles.traceFullWidth}`} data-testid="ctg-trace" data-display={display}>
      <div className={styles.traceFrame}>
        <div ref={inlineHost} className={styles.traceHost} data-testid="ctg-trace-viewer" aria-label={`${labels.trace}: ${title}`} />
        {status === 'loading' && <p className={styles.traceStatus}>{labels.loadingTrace}</p>}
        {status === 'error' && <p className={styles.traceStatus} role="alert">{labels.traceError}</p>}
        {thumbnail && status === 'ready' && (
          <button type="button" className={styles.traceEnlarge} onClick={openModal} data-testid="ctg-trace-enlarge">
            <span aria-hidden="true">⤢</span>
            <span>{labels.enlargeTrace}</span>
          </button>
        )}
      </div>
      {caption && <figcaption>{caption}</figcaption>}
      {thumbnail && (
        <dialog ref={dialog} className={styles.modal} data-testid="ctg-trace-modal" aria-label={title} onClose={() => disposeFhrViewerHost(modalHost.current)}>
          <div className={styles.modalHeader}>
            <strong>{caption || title}</strong>
            <button type="button" className={styles.modalClose} onClick={() => dialog.current?.close()} aria-label={labels.close}>
              ✕
            </button>
          </div>
          <div ref={modalHost} className={styles.modalTraceHost} />
        </dialog>
      )}
    </figure>
  )
}
